import { action, thunk } from 'easy-peasy'
import type { Action, Thunk } from 'easy-peasy'
import { borrowerPortalRequest } from '@mortgage-pos/ui/services/http'
import ApplicationClient from '@mortgage-pos/ui/services/ApplicationClient'
import { variation } from '@mortgage-pos/ui/services/featureFlags'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import {
  ALFIE_CREDIT_ORDER_STATUS,
  CreditReportType,
} from '@mortgage-pos/types'
import type {
  AlfieCreditHoldReason,
  AlfieCreditOrderResult,
  AlfieCreditOrderStatus,
} from '@mortgage-pos/types'
import type { StoreModel } from './index'

export type CreditPullType = 'soft' | 'hard' | 'test'

const CREDIT_REQUEST_FAILED_ERROR_DETAILS = 'Credit request failed'

function assertCreditOrderSucceeded(
  didOrder: boolean | undefined,
  creditPullType: Exclude<CreditPullType, 'test'>
) {
  if (didOrder === false) {
    throw new Error(`Failed to order ${creditPullType} credit`)
  }
}

export interface CreditModel {
  isOrderingCredit: boolean
  hasOrderedSimpleV2Credit: boolean
  orderCreditError: string | null
  creditHold: AlfieCreditHoldReason[] | null

  setIsOrderingCredit: Action<CreditModel, boolean>
  setHasOrderedSimpleV2Credit: Action<CreditModel, boolean>
  setOrderCreditError: Action<CreditModel, string | null>
  setCreditHold: Action<CreditModel, AlfieCreditHoldReason[] | null>

  orderCredit: Thunk<
    CreditModel,
    { creditPullType: CreditPullType; isTrimerge?: boolean },
    object,
    StoreModel,
    Promise<boolean>
  >

  orderAlfieCredit: Thunk<
    CreditModel,
    void,
    object,
    StoreModel,
    Promise<AlfieCreditOrderStatus | null>
  >

  orderSimpleV2Credit: Thunk<
    CreditModel,
    void,
    object,
    StoreModel,
    Promise<boolean>
  >
}

const credit = (): CreditModel => ({
  isOrderingCredit: false,
  hasOrderedSimpleV2Credit: false,
  orderCreditError: null,
  creditHold: null,

  setIsOrderingCredit: action((state, payload) => {
    state.isOrderingCredit = payload
  }),

  setHasOrderedSimpleV2Credit: action((state, payload) => {
    state.hasOrderedSimpleV2Credit = payload
  }),

  setOrderCreditError: action((state, payload) => {
    state.orderCreditError = payload
  }),

  setCreditHold: action((state, payload) => {
    state.creditHold = payload
  }),

  orderCredit: thunk(
    async (
      actions,
      { creditPullType, isTrimerge = false },
      { getState, getStoreState }
    ): Promise<boolean> => {
      if (getState().isOrderingCredit) {
        return false
      }

      actions.setIsOrderingCredit(true)
      actions.setOrderCreditError(null)

      try {
        if (creditPullType === 'test') {
          await borrowerPortalRequest(
            '/fannie/order-credit',
            { creditPullType: 'test' },
            {},
            'post'
          )
          return true
        }

        // TODO: Move soft/hard ordering behind a unified credit-ordering client
        // once the backend preserves today's soft-pull provider, consent, MRE,
        // publishing, and answer-update behavior through that shared path.
        if (creditPullType === 'soft') {
          const { applicationId } = getStoreState().application
          const result = await ApplicationClient.softPullCreditScore(
            applicationId,
            false,
            isTrimerge
          )
          assertCreditOrderSucceeded(!!result?.success, creditPullType)
          return true
        }

        const { applicationId } = getStoreState().application
        const didOrder = await ApplicationClient.pullCredit(
          applicationId,
          CreditReportType.Trimerge
        )
        assertCreditOrderSucceeded(didOrder, creditPullType)
        return true
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to order credit'
        actions.setOrderCreditError(message)
        throw error
      } finally {
        actions.setIsOrderingCredit(false)
      }
    }
  ),

  orderAlfieCredit: thunk(
    async (
      actions,
      _,
      { getState }
    ): Promise<AlfieCreditOrderStatus | null> => {
      if (getState().isOrderingCredit) {
        return null
      }

      actions.setIsOrderingCredit(true)
      actions.setOrderCreditError(null)
      actions.setCreditHold(null)

      try {
        const response = await borrowerPortalRequest(
          '/credit/order',
          {},
          {},
          'post'
        )
        const result: AlfieCreditOrderResult | undefined = response?.data

        if (result?.status === ALFIE_CREDIT_ORDER_STATUS.Held) {
          actions.setCreditHold(result.reasons)
          return result.status
        }
        if (
          result?.status !== ALFIE_CREDIT_ORDER_STATUS.Ordered &&
          result?.status !== ALFIE_CREDIT_ORDER_STATUS.Existing
        ) {
          throw new Error(
            `Failed to order credit: ${result?.reason ?? 'unknown'}`
          )
        }
        return result.status
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to order credit'
        actions.setOrderCreditError(message)
        throw error
      } finally {
        actions.setIsOrderingCredit(false)
      }
    }
  ),

  orderSimpleV2Credit: thunk(
    async (
      actions,
      _,
      { getState, getStoreState, getStoreActions }
    ): Promise<boolean> => {
      const { orderCredit, orderAlfieCredit, setHasOrderedSimpleV2Credit } =
        actions
      const { tracking, fannie } = getStoreActions()
      const { isOrderingCredit, hasOrderedSimpleV2Credit } = getState()
      const {
        answers: { mergedAnswers },
        questionnaire: { isSimpleV2FannieFlow },
        fannie: { isOrdering: isOrderingFannie },
      } = getStoreState()

      if (isOrderingCredit || isOrderingFannie || hasOrderedSimpleV2Credit) {
        return false
      }

      const isAlfieCreditOrder =
        !isSimpleV2FannieFlow &&
        Boolean(await variation(FEATURE_FLAG_TYPES.ENABLE_ALFIE_CREDIT_ORDER))
      // A resumed flow can land here without passing the consent screen.
      if (isSimpleV2FannieFlow) {
        actions.setIsOrderingCredit(true)
        try {
          await getStoreActions().questionnaire.resolveServerRobinhoodSoftPull()
        } finally {
          actions.setIsOrderingCredit(false)
        }
      }
      const { isRobinhoodSoftPullClient } = getStoreState().questionnaire
      const intendedCreditPullType: CreditPullType =
        isRobinhoodSoftPullClient || mergedAnswers.purchaseKnownAddress === 'No'
          ? 'soft'
          : 'hard'
      const appEnv = process.env.APP_ENV ?? ''
      const creditPullType: CreditPullType =
        ['local', 'qa'].includes(appEnv) && !isSimpleV2FannieFlow
          ? 'test'
          : intendedCreditPullType

      let didOrder: boolean
      let hasExistingCreditReport = false
      try {
        if (isSimpleV2FannieFlow) {
          didOrder = await fannie.orderCredit({ creditPullType })
        } else if (isAlfieCreditOrder) {
          const status = await orderAlfieCredit()
          hasExistingCreditReport =
            status === ALFIE_CREDIT_ORDER_STATUS.Existing
          didOrder =
            status === ALFIE_CREDIT_ORDER_STATUS.Ordered ||
            hasExistingCreditReport
        } else {
          didOrder = await orderCredit({
            creditPullType,
            isTrimerge: intendedCreditPullType === 'soft',
          })
        }
      } catch (error) {
        if (isSimpleV2FannieFlow) {
          actions.setOrderCreditError(CREDIT_REQUEST_FAILED_ERROR_DETAILS)
        }
        tracking.formErrored({
          errorType: 'credit_request_failed',
          errorMessage: CREDIT_REQUEST_FAILED_ERROR_DETAILS,
          errorDetails: CREDIT_REQUEST_FAILED_ERROR_DETAILS,
        })
        return false
      }

      if (!didOrder && isSimpleV2FannieFlow) {
        actions.setOrderCreditError(CREDIT_REQUEST_FAILED_ERROR_DETAILS)
      }

      if (didOrder) {
        setHasOrderedSimpleV2Credit(true)
        if (!hasExistingCreditReport) {
          tracking.elementLoaded({
            elementName: 'credit_request_completed',
            location: 'simple_v2_lifecycle',
          })
        }
        if (isSimpleV2FannieFlow) await fannie.orderReport(null)
      }

      return didOrder
    }
  ),
})

export default credit

export function isSimpleV2CreditHeld(
  answers: {
    coBorrowerCreditScore?: number | string | null
    hasSavedCoBorrowerSSN?: boolean | null
  },
  creditHold: AlfieCreditHoldReason[] | null
): boolean {
  if (creditHold) return true

  const coBorrowerCreditScore = Number(answers.coBorrowerCreditScore)
  return (
    Number.isFinite(coBorrowerCreditScore) &&
    coBorrowerCreditScore > 0 &&
    answers.hasSavedCoBorrowerSSN !== true
  )
}
